/** SUNTRONIX ONE — MFA (TOTP + Recovery Codes) settings.
 *  Enrol / verify / view recovery codes / disable. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { ShieldCheck, ShieldOff, Copy } from "lucide-react";

export default function MFASettings() {
  const [status, setStatus] = useState(null);
  const [enroll, setEnroll] = useState(null);   // {otpauth_url, secret, account}
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setStatus((await api.get("/mfa/status")).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEnroll = async () => {
    setBusy(true);
    try { setEnroll((await api.post("/mfa/enroll/start")).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to start enrolment"); }
    finally { setBusy(false); }
  };
  const verifyEnroll = async () => {
    if (!code.trim()) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/mfa/enroll/verify", { code });
      setRecovery(data.recovery_codes);
      toast.success("MFA enrolled — save your recovery codes now.");
      setEnroll(null); setCode(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    if (!code.trim()) { toast.error("Enter current TOTP"); return; }
    setBusy(true);
    try {
      await api.post("/mfa/disable", { code });
      toast.success("MFA disabled"); setCode(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disable failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="mfa-settings">
      <PageHeader eyebrow="Account · Security" title="Multi-factor authentication"
        context="TOTP + recovery codes for your Suntronix account." testId="mfa-header" />

      <Section title="Status" testId="mfa-status">
        <div className="st-panel p-4 flex items-center gap-3" data-testid="mfa-status-card">
          {status?.enrolled
            ? <ShieldCheck className="h-6 w-6 text-emerald-600" strokeWidth={1.6} />
            : <ShieldOff className="h-6 w-6 text-amber-500" strokeWidth={1.6} />}
          <div>
            <div className="text-sm font-medium">
              {status?.enrolled ? "Enrolled" : "Not enrolled"}
              {status?.enforced && <span className="ml-2 text-[11px] text-amber-700">Required for your role</span>}
            </div>
            <div className="text-[11px] text-slate-500">
              {status?.enrolled_at && `Enrolled ${new Date(status.enrolled_at).toLocaleDateString()} · `}
              {status?.recovery_codes_remaining ?? 0} recovery codes remaining
            </div>
          </div>
        </div>
      </Section>

      {recovery && (
        <Section title="Recovery codes — save these now" hint="Each code is single-use. They will not be shown again." testId="mfa-recovery">
          <div className="st-panel p-3 grid grid-cols-2 gap-2 font-mono text-[12px]" data-testid="mfa-recovery-list">
            {recovery.map((c) => <div key={c} className="p-1.5 bg-slate-50 rounded">{c}</div>)}
          </div>
          <Button variant="outline" className="mt-2"
            onClick={() => { navigator.clipboard.writeText(recovery.join("\n")); toast.success("Copied"); }}
            data-testid="mfa-copy-codes">
            <Copy className="h-3.5 w-3.5 mr-1" strokeWidth={1.6} /> Copy all
          </Button>
        </Section>
      )}

      {!status?.enrolled && !enroll && (
        <Section title="Enrol" testId="mfa-enrol">
          <Button onClick={startEnroll} disabled={busy} data-testid="mfa-enrol-start"
            style={{ background: "var(--st-accent)", color: "white" }}>
            Start MFA enrolment
          </Button>
        </Section>
      )}

      {enroll && (
        <Section title="Scan with your authenticator" testId="mfa-scan">
          <div className="st-panel p-4 space-y-2 text-[12px]" data-testid="mfa-scan-card">
            <div className="text-slate-700 break-all">Manual key: <span className="font-mono">{enroll.secret}</span></div>
            <div className="text-slate-500 text-[11px] break-all">otpauth URL: {enroll.otpauth_url}</div>
            <div className="pt-2">
              <label className="text-[11px] text-slate-600">6-digit code from your authenticator</label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={8} data-testid="mfa-code" />
            </div>
            <Button onClick={verifyEnroll} disabled={busy} data-testid="mfa-verify"
              style={{ background: "var(--st-accent)", color: "white" }}>
              Verify & enable
            </Button>
          </div>
        </Section>
      )}

      {status?.enrolled && (
        <Section title="Disable MFA" hint="Requires a live TOTP code" testId="mfa-disable">
          <div className="st-panel p-4 space-y-2">
            <Input placeholder="Current 6-digit TOTP" value={code} onChange={(e) => setCode(e.target.value)}
              maxLength={8} data-testid="mfa-disable-code" />
            <Button variant="outline" onClick={disable} disabled={busy} data-testid="mfa-disable-btn"
              className="text-rose-600">
              <ShieldOff className="h-3.5 w-3.5 mr-1" strokeWidth={1.6} /> Disable MFA
            </Button>
          </div>
        </Section>
      )}
    </div>
  );
}
